import React, { useState, useEffect, useRef } from 'react';
import { Shield, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  Checkbox,
  DatePicker,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Label,
  RadioGroup,
  RadioGroupItem,
} from '@roger/ui-components';
import type {
  EpisodeUnsentBillingCyclesResponse,
  PatientInsuranceResponse,
} from '../../services/api/types.gen';
import { listEpisodeUnsentBillingCycles } from '../../services/api';
import { useToast } from '../../hooks/useToastContext';
import {
  formatDateToISO,
  formatDateToMMDDYYYY,
  parseISOToLocalDate,
} from '../../utils/dateUtils';
import { patientInsuranceSectionPath } from '../../utils/navigation';
import { EligibilityCheckSummary } from '../eligibility/EligibilityCheckSummary';
import { OtherAgencyPeriodsOfCare } from '../eligibility/OtherAgencyPeriodsOfCare';
import { latestCmsAnswer } from '../eligibility/eligibilityStatus';
import { usePatientEligibilityChecks } from '../../hooks/usePatientEligibilityChecks';
import { EpisodeInsuranceVoidCyclesList } from './EpisodeInsuranceVoidCyclesList';

/**
 * Why the insurance is changing. Mirrors the medication editor's
 * "Error Correction" / "Medication Change" choice: a correction voids unsent
 * claims (their periods are rebuilt once due); a change may bill the old
 * insurance up to the switch.
 */
type SwitchReason = 'correction' | 'change';

/** The billing action saved with the switch; at most one field is set. */
export interface EpisodeInsuranceSwitchOptions {
  /** Error correction: unsent cycles under the previous insurance to void (rebuilt once due). */
  voidBillingCycleIds?: string[];
  /** Insurance change: last day (YYYY-MM-DD) the previous insurance bills. */
  closeOutEndDate?: string;
}

interface EpisodeInsuranceSelectorProps {
  open: boolean;
  onClose: () => void;
  /** Save the insurance with the chosen billing action, if any. */
  onConfirm: (
    insuranceId: string,
    options: EpisodeInsuranceSwitchOptions
  ) => Promise<void>;
  patientInsurances: PatientInsuranceResponse[];
  currentInsuranceId: string | null | undefined;
  insurancesLoading?: boolean;
  patientId?: string;
  /** Needed to offer the void step; without it the step is skipped. */
  episodeId?: string;
}

export const EpisodeInsuranceSelector: React.FC<
  EpisodeInsuranceSelectorProps
> = ({
  open,
  onClose,
  onConfirm,
  patientInsurances,
  currentInsuranceId,
  insurancesLoading,
  patientId,
  episodeId,
}) => {
  const navigate = useNavigate();
  const { error: showError } = useToast();
  // Gated on `open`: this dialog is rendered unconditionally from PatientDetail
  // and hides itself, so an ungated fetch would run on every chart open.
  const {
    checks,
    latestFor,
    resolving: checksResolving,
    failed: checksFailed,
  } = usePatientEligibilityChecks(patientId, open);
  const [selectedInsuranceId, setSelectedInsuranceId] = useState<string | null>(
    null
  );
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // null until loaded, or after a failed load.
  const [unsent, setUnsent] =
    useState<EpisodeUnsentBillingCyclesResponse | null>(null);
  // While the list is still loading, Save waits: saving before it resolves
  // would skip the billing action the user is entitled to.
  const [unsentLoading, setUnsentLoading] = useState(false);
  // A failed load blocks the switch: saving without the claims data would
  // leave the old insurance's cycles untouched with no chance to choose.
  const [unsentLoadFailed, setUnsentLoadFailed] = useState(false);
  // Bumped by Retry to run the load again without reopening the dialog.
  const [unsentLoadAttempt, setUnsentLoadAttempt] = useState(0);
  const [reason, setReason] = useState<SwitchReason | null>(null);
  const [checkedVoidIds, setCheckedVoidIds] = useState<Set<string>>(
    () => new Set()
  );
  const [closeOutEnabled, setCloseOutEnabled] = useState(true);
  const [closeOutEnd, setCloseOutEnd] = useState<Date | undefined>(undefined);
  // True once a load has succeeded since the dialog opened; a later reload
  // then keeps the user's checked ids instead of re-checking every cycle.
  const hasLoadedUnsentRef = useRef(false);

  // Reset selection when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedInsuranceId(null);
      setShowConfirmation(false);
      setReason(null);
      setCloseOutEnabled(true);
      hasLoadedUnsentRef.current = false;
    }
  }, [open]);

  // Defaults follow the loaded data: the first load checks every unsent cycle; a
  // reload (Retry, or after a refused save) keeps the selection for cycles still
  // listed and leaves new ones unchecked; a failed load leaves it alone. The
  // close-out end always resets: a reload can move the start past the chosen end.
  useEffect(() => {
    if (unsent) {
      const ids = unsent.cycles.map(cycle => cycle.id);
      const keepSelection = hasLoadedUnsentRef.current;
      setCheckedVoidIds(previous =>
        keepSelection
          ? new Set(ids.filter(id => previous.has(id)))
          : new Set(ids)
      );
      if (unsent.close_out_external) {
        // The switch refuses a close-out the External EMR bills.
        setCloseOutEnabled(false);
      } else if (!keepSelection) {
        setCloseOutEnabled(true);
      }
      hasLoadedUnsentRef.current = true;
    }
    setCloseOutEnd(
      unsent
        ? parseISOToLocalDate(unsent.close_out_default_end_date)
        : undefined
    );
  }, [unsent]);

  // The unsent cycles under the current insurance, fetched once per open (or
  // per Retry). Whoever may change the insurance may void them, so
  // there is no separate permission gate. A failed load is reported with a
  // toast and blocks Save until a retry succeeds.
  const shouldLoadUnsent =
    open && Boolean(episodeId) && Boolean(currentInsuranceId);
  useEffect(() => {
    if (!shouldLoadUnsent || !episodeId) {
      setUnsent(null);
      setUnsentLoading(false);
      setUnsentLoadFailed(false);
      return;
    }
    let cancelled = false;
    setUnsentLoading(true);
    setUnsentLoadFailed(false);
    (async () => {
      try {
        const res = await listEpisodeUnsentBillingCycles({
          path: { episode_id: episodeId },
        });
        if (cancelled) return;
        if (res.error) {
          throw res.error;
        }
        setUnsent(res.data ?? null);
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to load unsent billing cycles:', error);
        setUnsent(null);
        setUnsentLoadFailed(true);
        showError(
          "Unable to load this episode's billing cycles. Please retry, and contact support if it keeps failing."
        );
      } finally {
        if (!cancelled) setUnsentLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shouldLoadUnsent, episodeId, unsentLoadAttempt, showError]);

  const handleInsuranceClick = (insuranceId: string) => {
    // Don't allow selecting the already-assigned insurance
    if (insuranceId === currentInsuranceId) {
      return;
    }
    setSelectedInsuranceId(insuranceId);
    setShowConfirmation(true);
  };

  const handleAddEditInsurancesClick = () => {
    if (!patientId) return;
    onClose();
    navigate(patientInsuranceSectionPath(patientId));
  };

  const toggleVoidId = (cycleId: string, checked: boolean) => {
    setCheckedVoidIds(previous => {
      const next = new Set(previous);
      if (checked) {
        next.add(cycleId);
      } else {
        next.delete(cycleId);
      }
      return next;
    });
  };

  const selectedInsurance = patientInsurances.find(
    ins => ins.id === selectedInsuranceId
  );
  const currentInsurance = patientInsurances.find(
    ins => ins.id === currentInsuranceId
  );
  const oldInsuranceName =
    currentInsurance?.insurance_name ?? 'the current insurance';

  // The close-out range: from the day after the episode's last live claim under
  // any insurance (or the episode start) through the chosen end. The default end
  // is already capped at today and the episode end, so it is also the latest end
  // allowed. An empty range means the old insurance is fully billed.
  const closeOutStart = unsent
    ? parseISOToLocalDate(unsent.close_out_start_date)
    : undefined;
  const closeOutMaxEnd = unsent
    ? parseISOToLocalDate(unsent.close_out_default_end_date)
    : undefined;
  const closeOutApplies =
    closeOutStart !== undefined &&
    closeOutMaxEnd !== undefined &&
    closeOutStart.getTime() <= closeOutMaxEnd.getTime();

  const switchOptions = (): EpisodeInsuranceSwitchOptions => {
    if (reason === 'correction') {
      const ids = (unsent?.cycles ?? [])
        .map(cycle => cycle.id)
        .filter(id => checkedVoidIds.has(id));
      return ids.length > 0 ? { voidBillingCycleIds: ids } : {};
    }
    if (
      reason === 'change' &&
      closeOutApplies &&
      closeOutEnabled &&
      closeOutEnd
    ) {
      return { closeOutEndDate: formatDateToISO(closeOutEnd) };
    }
    return {};
  };

  const handleSave = async () => {
    if (!selectedInsuranceId) return;
    // With no current insurance there is nothing to void or close out, so no
    // reason is asked for.
    if (currentInsuranceId && !reason) return;

    setIsSubmitting(true);
    try {
      await onConfirm(selectedInsuranceId, switchOptions());
      onClose();
    } catch (error) {
      // The caller has already shown the error; the dialog stays open. A refused
      // switch (e.g. a 409: a cycle changed or now overlaps) may have changed the
      // unsent cycles, so reload them rather than let the next Save resend stale
      // ids; the `[unsent]` effect keeps the checked ids and resets the end.
      console.error('Failed to update episode insurance:', error);
      if (shouldLoadUnsent) {
        setUnsentLoadAttempt(attempt => attempt + 1);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    setShowConfirmation(false);
    setSelectedInsuranceId(null);
    setReason(null);
  };

  const renderBillingAction = () => {
    if (!reason) return null;
    if (unsentLoading) {
      return <p className='text-sm text-muted-foreground'>Loading claims…</p>;
    }
    if (!unsent) {
      return (
        <div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between'>
          <p className='text-sm text-destructive'>
            Unable to load claims. Retry, or contact support if it keeps
            failing.
          </p>
          <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={() => setUnsentLoadAttempt(attempt => attempt + 1)}
          >
            Retry
          </Button>
        </div>
      );
    }
    if (reason === 'correction') {
      return (
        <EpisodeInsuranceVoidCyclesList
          cycles={unsent.cycles}
          checkedIds={checkedVoidIds}
          onToggle={toggleVoidId}
          oldInsuranceName={oldInsuranceName}
          newInsuranceName={selectedInsurance?.insurance_name ?? ''}
          disabled={isSubmitting}
        />
      );
    }
    if (!closeOutApplies) {
      return (
        <p className='text-sm text-muted-foreground'>
          No additional claim is needed for {oldInsuranceName}.
        </p>
      );
    }
    return (
      <div className='rounded-lg border border-border p-3 space-y-3'>
        <div className='flex items-start gap-3'>
          <Checkbox
            id='close-out-old-insurance'
            checkboxSize='default'
            checked={closeOutEnabled}
            disabled={isSubmitting}
            onCheckedChange={checked => setCloseOutEnabled(checked === true)}
          />
          <label
            htmlFor='close-out-old-insurance'
            className='text-sm font-medium'
          >
            Create claim for {formatDateToMMDDYYYY(closeOutStart)} –{' '}
            {formatDateToMMDDYYYY(closeOutEnd)} under {oldInsuranceName}?
          </label>
        </div>
        {unsent.close_out_external && unsent.close_out_external_detail && (
          <p
            className='text-sm text-muted-foreground'
            data-testid='close-out-external-note'
          >
            {unsent.close_out_external_detail}
          </p>
        )}
        <div className='flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3'>
          <Label htmlFor='close-out-end-date' className='text-sm'>
            Through
          </Label>
          <DatePicker
            id='close-out-end-date'
            aria-label='Last day under the old insurance'
            date={closeOutEnd}
            onDateChange={next => next && setCloseOutEnd(next)}
            allowClear={false}
            minDate={closeOutStart}
            maxDate={closeOutMaxEnd}
            datePickerSize='default'
            disabled={!closeOutEnabled || isSubmitting}
            className='w-full sm:w-56'
          />
        </div>
        <p className='text-xs text-muted-foreground'>
          Last day {oldInsuranceName} covers. Change it if the insurance changed
          before today.
        </p>
      </div>
    );
  };

  if (showConfirmation && selectedInsurance) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className='w-[calc(100vw-2rem)] sm:max-w-lg max-h-[90vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>Confirm Insurance Selection</DialogTitle>
            <DialogDescription>
              You are about to set this insurance as the primary for this
              episode.
            </DialogDescription>
          </DialogHeader>

          <div className='rounded-lg border border-border p-4 space-y-2'>
            <p className='font-medium text-sm'>
              {selectedInsurance.insurance_name}
            </p>
            <div className='text-xs text-muted-foreground space-y-1'>
              {selectedInsurance.plan_name && (
                <p>Plan: {selectedInsurance.plan_name}</p>
              )}
              {selectedInsurance.policy_number && (
                <p>Policy: {selectedInsurance.policy_number}</p>
              )}
              {selectedInsurance.effective_start && (
                <p>
                  Effective:{' '}
                  {formatDateToMMDDYYYY(selectedInsurance.effective_start)}
                  {selectedInsurance.effective_end &&
                    ` – ${formatDateToMMDDYYYY(selectedInsurance.effective_end)}`}
                </p>
              )}
            </div>
          </div>

          {currentInsuranceId && (
            <div className='space-y-2'>
              <Label className='text-sm font-medium'>
                Why is the insurance changing?
              </Label>
              <RadioGroup
                value={reason ?? ''}
                onValueChange={value => setReason(value as SwitchReason)}
                disabled={isSubmitting}
                className='gap-3'
              >
                {(
                  [
                    {
                      value: 'correction',
                      label: 'Error Correction',
                      hint: 'The insurance on this episode was entered wrong.',
                    },
                    {
                      value: 'change',
                      label: 'Insurance Change',
                      hint: "The patient's coverage changed.",
                    },
                  ] as const
                ).map(option => (
                  <div key={option.value} className='flex items-start gap-2'>
                    <RadioGroupItem
                      value={option.value}
                      id={`switch-reason-${option.value}`}
                      className='mt-0.5'
                    />
                    <Label
                      htmlFor={`switch-reason-${option.value}`}
                      className='font-normal cursor-pointer'
                    >
                      {option.label}
                      <span className='block text-xs text-muted-foreground'>
                        {option.hint}
                      </span>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          )}

          {renderBillingAction()}

          <DialogFooter>
            <Button
              type='button'
              variant='outline'
              onClick={handleBack}
              disabled={isSubmitting}
            >
              Back
            </Button>
            <Button
              type='button'
              onClick={handleSave}
              disabled={
                isSubmitting ||
                unsentLoading ||
                (Boolean(currentInsuranceId) &&
                  (!reason || unsentLoadFailed || !unsent))
              }
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  const hasCurrentInsurance = patientInsurances.some(
    ins => ins.id === currentInsuranceId
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className='w-[calc(100vw-2rem)] sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>Select Episode Insurance</DialogTitle>
          <DialogDescription>
            {!hasCurrentInsurance && currentInsuranceId === null && (
              <span className='block mb-2 text-amber-600 dark:text-amber-500 font-medium'>
                No insurance currently selected for this episode.
              </span>
            )}
            Click an insurance to attach it as the primary for this episode.{' '}
            {patientId && (
              <button
                type='button'
                onClick={handleAddEditInsurancesClick}
                className='text-primary hover:underline'
              >
                Click here to add or edit the patient's insurances.
              </button>
            )}
          </DialogDescription>
        </DialogHeader>

        {!checksResolving && !checksFailed && (
          <OtherAgencyPeriodsOfCare check={latestCmsAnswer(checks)} />
        )}

        {insurancesLoading ? (
          <p className='text-sm text-muted-foreground py-6 text-center'>
            Loading insurances…
          </p>
        ) : patientInsurances.length === 0 ? (
          <p className='text-sm text-muted-foreground py-6 text-center'>
            No insurance policies on file.
          </p>
        ) : (
          <div className='divide-y max-h-[400px] overflow-y-auto'>
            {patientInsurances.map(ins => {
              const isCurrent = ins.id === currentInsuranceId;
              return (
                <button
                  key={ins.id}
                  type='button'
                  onClick={() => handleInsuranceClick(ins.id)}
                  disabled={isCurrent}
                  className='w-full py-3 px-3 flex items-center gap-3 hover:bg-muted transition-colors text-left rounded-md disabled:opacity-60 disabled:cursor-not-allowed'
                >
                  <Shield className='h-4 w-4 text-muted-foreground shrink-0' />
                  <div className='flex-1 min-w-0'>
                    <div className='flex items-center gap-2'>
                      <p className='text-sm font-medium truncate'>
                        {ins.insurance_name}
                      </p>
                      {isCurrent && (
                        <span className='inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-primary/10 text-primary text-[10px] font-medium'>
                          <Check className='h-3 w-3' />
                          Current
                        </span>
                      )}
                    </div>
                    <p className='text-xs text-muted-foreground'>
                      {[
                        ins.plan_name,
                        ins.policy_number
                          ? `Policy: ${ins.policy_number}`
                          : null,
                        ins.effective_start
                          ? `${formatDateToMMDDYYYY(ins.effective_start)}${ins.effective_end ? ` – ${formatDateToMMDDYYYY(ins.effective_end)}` : ''}`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                    {/* Coverage is most likely to have gone stale exactly here,
                        at a new episode or recert. */}
                    <div className='mt-1'>
                      <EligibilityCheckSummary
                        check={latestFor(ins)}
                        loading={checksResolving}
                        failed={checksFailed}
                      />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button type='button' variant='outline' onClick={onClose}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
